import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import HealthFreeSoloSelect from './HealthFreeSoloSelect';
import HealthAttachmentsSection from './HealthAttachmentsSection';
import HealthFilePreview from './HealthFilePreview';
import HealthDateTimeField from './HealthDateTimeField';
import { isBillType } from './healthDownloadUtils';

export default function HealthFileEditDialog({
  open,
  file,
  onClose,
  onSaved,
  request,
  fieldOptions,
}) {
  const [form, setForm] = useState({
    file_name: '',
    bill_number: '',
    report_type: '',
    type: '',
    hospital_name: '',
    date: '',
    tags: [],
  });
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [localPreview, setLocalPreview] = useState(null);

  useEffect(() => {
    if (!open) setLocalPreview(null);
  }, [open]);

  useEffect(() => {
    if (!open || !file) return;
    setForm({
      file_name: file.file_name || '',
      bill_number: file.bill_number || '',
      report_type: file.report_type || '',
      type: file.type || '',
      hospital_name: file.hospital_name || '',
      date: file.date || '',
      tags: file.tags || [],
    });
    setAttachments(Array.isArray(file.attachments) ? file.attachments : []);
    setPendingFiles([]);
    setError('');
    setTagInput('');
    request('/api/health/tags')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setTagOptions(data.tags || []);
      })
      .catch(() => {});
  }, [open, file, request]);

  async function removeExisting(key) {
    if (!file) return;
    const res = await request(`/api/health/files/${file.id}/attachments?key=${encodeURIComponent(key)}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      const updated = await res.json();
      setAttachments(updated.attachments || []);
      onSaved(updated);
    }
  }

  async function handleSave() {
    if (!file) return;
    if (!form.type.trim()) {
      setError('Type is required');
      return;
    }
    if (!form.date) {
      setError('Date and time are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const tagNames = [];
      for (const t of form.tags) {
        const name = typeof t === 'string' ? t : t.name;
        if (name && name.trim()) tagNames.push(name.trim());
      }

      if (pendingFiles.length > 0) {
        const formData = new FormData();
        if (form.file_name.trim()) formData.append('file_name', form.file_name.trim());
        else formData.append('file_name', '');
        formData.append('bill_number', form.bill_number.trim());
        formData.append('report_type', form.report_type.trim());
        formData.append('type', form.type.trim());
        formData.append('hospital_name', form.hospital_name.trim());
        formData.append('date', form.date);
        formData.append('tags', JSON.stringify(tagNames));
        for (let i = 0; i < pendingFiles.length; i += 1) {
          formData.append('files', pendingFiles[i]);
        }
        const res = await request(`/api/health/files/${file.id}`, {
          method: 'PATCH',
          body: formData,
          headers: {},
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || 'Failed to update file');
          return;
        }
        onSaved(await res.json());
      } else {
        const res = await request(`/api/health/files/${file.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            file_name: form.file_name.trim() || null,
            bill_number: form.bill_number.trim() || null,
            report_type: form.report_type.trim() || null,
            type: form.type.trim(),
            hospital_name: form.hospital_name.trim() || null,
            date: form.date,
            tags: tagNames,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || 'Failed to update file');
          return;
        }
        onSaved(await res.json());
      }
      onClose();
    } catch (e) {
      setError(e.message || 'Request failed');
    } finally {
      setSaving(false);
    }
  }

  if (!file) return null;

  const options = fieldOptions || { types: [], report_types: [], hospitals: [] };
  const showBillNumber = isBillType(form.type);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit file</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="File name (optional)"
              fullWidth
              value={form.file_name}
              onChange={(e) => setForm((f) => ({ ...f, file_name: e.target.value }))}
            />
            <HealthFreeSoloSelect
              label="Report type"
              value={form.report_type}
              options={options.report_types || []}
              onChange={(v) => setForm((f) => ({ ...f, report_type: v }))}
            />
            <HealthFreeSoloSelect
              label="Type"
              value={form.type}
              options={options.types || []}
              onChange={(v) => setForm((f) => ({ ...f, type: v }))}
              required
            />
            {showBillNumber && (
              <TextField
                label="Bill number"
                fullWidth
                value={form.bill_number}
                onChange={(e) => setForm((f) => ({ ...f, bill_number: e.target.value }))}
              />
            )}
            <HealthFreeSoloSelect
              label="Hospital"
              value={form.hospital_name}
              options={options.hospitals || []}
              onChange={(v) => setForm((f) => ({ ...f, hospital_name: v }))}
            />
            <HealthDateTimeField
              label="Document date"
              required
              value={form.date}
              onChange={(v) => setForm((f) => ({ ...f, date: v }))}
            />
            <Autocomplete
              multiple
              freeSolo
              options={tagOptions}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt && opt.name ? opt.name : ''))}
              value={form.tags}
              inputValue={tagInput}
              onInputChange={(_, v) => setTagInput(v)}
              onChange={(_, v) => setForm((f) => ({ ...f, tags: v }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const label = typeof option === 'string' ? option : (option && option.name ? option.name : '');
                  if (!label) return null;
                  const tagProps = getTagProps({ index });
                  return <Chip {...tagProps} key={`${label}-${index}`} label={label} size="small" />;
                })
              }
              renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tags" />}
            />
            <HealthAttachmentsSection
              title="Attachments"
              existingAttachments={attachments}
              pendingFiles={pendingFiles}
              onPendingChange={setPendingFiles}
              onRemoveExisting={removeExisting}
              onPreview={setLocalPreview}
              request={request}
              disabled={saving}
              healthFileId={file.id}
              onPdfCreated={(updated) => {
                setAttachments(updated.attachments || []);
                onSaved(updated);
              }}
            />
            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <HealthFilePreview preview={localPreview} onClose={() => setLocalPreview(null)} request={request} />
    </>
  );
}
